import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import ClearAllOutlined from '@mui/icons-material/ClearAllOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { executeTestLabIngest, getTestLabSources, TestLabSourceSlide } from 'api/testLabIngest';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { difference, filter, flatMap, join, keys, map, size, take, uniq, values } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { getQueryErrorMessage } from 'utils/httpError';

interface Props {
  labId: string;
  studyId: string;
  studyName: string;
}

const SourceTable = ({ labId, studyId, studyName, active }: Props & { active: boolean }) => {
  const confirm = useConfirmation();
  const [search, setSearch] = useState('');
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [selected, setSelected] = useState<Record<string, TestLabSourceSlide>>({});
  const [confirming, setConfirming] = useState(false);
  const pageSlideIds = useRef<Record<string, string[]>>({});
  const pageKey = JSON.stringify([cursors[size(cursors) - 1], search]);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const sourceQuery = useQuery({
    queryKey: ['testLabSources', labId, studyId, cursors[size(cursors) - 1], search],
    queryFn: () => getTestLabSources({ labId, studyId, cursor: cursors[size(cursors) - 1], search }),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: active,
    onSuccess: (result) => {
      const missingIds = difference(pageSlideIds.current[pageKey] || [], map(result.slides, 'id'));
      pageSlideIds.current[pageKey] = map(result.slides, 'id');
      setSelected((previous) => {
        const next = { ...previous };
        for (const missingId of missingIds)
          if (next[missingId])
            next[missingId] = {
              ...next[missingId],
              canIngest: false,
              unavailableReason: 'Source no longer accessible or available.',
            };
        for (const slide of result.slides) if (next[slide.id]) next[slide.id] = slide;
        return next;
      });
    },
  });
  const mutation = useMutation(executeTestLabIngest, {
    onSuccess: () => {
      setSelected({});
      sourceQuery.refetch();
    },
  });
  const busy = mutation.isLoading || confirming;
  const blockedSelectionCount = size(filter(values(selected), (slide) => !slide.canIngest));
  const execute = async () => {
    setConfirming(true);
    const sourceStudyNames = uniq(flatMap(values(selected), (slide) => map(slide.sourceStudies, 'name')));
    const accepted = await confirm({
      title: `Ingest into ${studyName}`,
      text: `Create ${size(selected)} test copies in ${studyName} from ${join(take(sourceStudyNames, 3), ', ')}${
        size(sourceStudyNames) > 3 ? ` and ${size(sourceStudyNames) - 3} other studies` : ''
      }? Original slides remain unchanged. Copies are marked not for training.`,
      confirmButtonProps: { title: 'Ingest', color: 'primary' },
      cancelButtonProps: { color: 'inherit' },
    });
    if (!mounted.current) return;
    setConfirming(false);
    if (accepted) mutation.mutate({ labId, studyId, sourceSlideIds: keys(selected) });
  };
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2">Destination: {studyName}</Typography>
      <TextField
        label="Search slides"
        type="search"
        size="small"
        value={search}
        disabled={busy}
        onChange={(event) => {
          setSearch(event.target.value);
          setCursors([undefined]);
          setSelected({});
          mutation.reset();
        }}
      />
      {sourceQuery.isInitialLoading ? (
        <CircularProgress size={20} aria-label="Loading source slides" />
      ) : sourceQuery.isError ? (
        <Alert severity="error">{getQueryErrorMessage(sourceQuery.error, 'Source slides could not be loaded.')}</Alert>
      ) : (
        <TableContainer sx={{ maxHeight: 200 }}>
          <Table stickyHeader size="small" aria-label="Slides from other studies" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="none" sx={{ width: 80, minWidth: 80, textAlign: 'center' }}>
                  Select
                </TableCell>
                <TableCell>Slide</TableCell>
                <TableCell>Source study</TableCell>
                <TableCell>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(sourceQuery.data?.slides || [], (slide) => (
                <TableRow key={slide.id}>
                  <TableCell padding="none" sx={{ textAlign: 'center' }}>
                    <Checkbox
                      inputProps={{ 'aria-label': `Select ${slide.fileName || slide.id}` }}
                      checked={Boolean(selected[slide.id])}
                      disabled={busy || ((!slide.canIngest || size(selected) >= 100) && !selected[slide.id])}
                      onChange={(event) => {
                        setSelected((previous) => {
                          const next = { ...previous };
                          if (event.target.checked) next[slide.id] = slide;
                          else delete next[slide.id];
                          return next;
                        });
                        mutation.reset();
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>{slide.fileName || slide.id}</TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {join(map(slide.sourceStudies, 'name'), ', ')}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: 'anywhere' }}>
                    {slide.canIngest
                      ? slide.sourceKind === 'original_upload'
                        ? 'Original upload'
                        : 'Stored slide asset'
                      : slide.unavailableReason}
                  </TableCell>
                </TableRow>
              ))}
              {!size(sourceQuery.data?.slides) && (
                <TableRow>
                  <TableCell colSpan={4}>No accessible source slides.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {mutation.isError && (
        <Alert severity="error">{getQueryErrorMessage(mutation.error, 'Test ingest could not be queued.')}</Alert>
      )}
      {mutation.isSuccess && (
        <Alert severity={size(mutation.data.failedJobIds) ? 'warning' : 'success'}>
          {size(mutation.data.jobIds)} ingest jobs queued.
          {size(mutation.data.failedJobIds) ? ` ${size(mutation.data.failedJobIds)} jobs failed to start.` : ''}
        </Alert>
      )}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption">{size(selected)} selected</Typography>
        {blockedSelectionCount > 0 && (
          <Typography variant="caption" color="error">
            {blockedSelectionCount} unavailable selections
          </Typography>
        )}
        <Tooltip title="Deselect all source slides">
          <span>
            <IconButton
              color="inherit"
              aria-label="Deselect all source slides"
              disabled={busy || !size(selected)}
              onClick={() => {
                setSelected({});
                mutation.reset();
              }}
            >
              <ClearAllOutlined />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Previous source page">
          <span>
            <IconButton
              color="inherit"
              aria-label="Previous source page"
              disabled={busy || size(cursors) === 1}
              onClick={() => setCursors((previous) => previous.slice(0, -1))}
            >
              <ArrowBackOutlined />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Next source page">
          <span>
            <IconButton
              color="inherit"
              aria-label="Next source page"
              disabled={busy || !sourceQuery.data?.nextCursor}
              onClick={() => setCursors((previous) => [...previous, sourceQuery.data?.nextCursor || undefined])}
            >
              <ArrowForwardOutlined />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refresh source slides">
          <IconButton
            color="inherit"
            aria-label="Refresh source slides"
            disabled={busy}
            onClick={() => sourceQuery.refetch()}
          >
            <RefreshOutlined />
          </IconButton>
        </Tooltip>
      </Stack>
      <Button
        color="primary"
        variant="contained"
        startIcon={<FileCopyOutlined />}
        disabled={busy || sourceQuery.isFetching || sourceQuery.isError || !size(selected) || blockedSelectionCount > 0}
        onClick={execute}
        sx={{ alignSelf: 'flex-start' }}
      >
        Ingest into {studyName}
      </Button>
    </Stack>
  );
};

export const TestLabSourceIngest = (props: Props) => {
  const [enabled, setEnabled] = useState(false);
  const [opened, setOpened] = useState(false);
  return (
    <Stack spacing={1.5}>
      <FormControlLabel
        control={
          <Checkbox
            checked={enabled}
            onChange={(event) => {
              setEnabled(event.target.checked);
              setOpened(true);
            }}
          />
        }
        label="Show slides from other studies in this lab"
      />
      {opened && (
        <Box hidden={!enabled}>
          <SourceTable key={`${props.labId}:${props.studyId}`} active={enabled} {...props} />
        </Box>
      )}
    </Stack>
  );
};
